import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  adminReviewsMetaSelector,
  adminReviewsSelector,
  getAdminReviews,
} from 'src/store/adminReviews';

import { ROUTE } from 'src/constants';

export const useBuild = () => {
  const { t } = useTranslation('adminBuilder');

  const dispatch = useDispatch();

  const adminReviews = useSelector(adminReviewsSelector);
  const adminReviewsMeta = useSelector(adminReviewsMetaSelector);

  const [searchParams, setSearchParams] = useSearchParams();

  const [filtersData, setFiltersData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const convertedParams = Object.fromEntries(searchParams.entries());

    setFiltersData(convertedParams);
    setCurrentPage(Number(convertedParams?.page) || 1);

    dispatch(getAdminReviews(convertedParams));
  }, [dispatch, searchParams]);

  const onSubmitFilters = useCallback(
    (filters) => {
      setCurrentPage(1);

      dispatch(getAdminReviews({ ...filters }));
    },
    [dispatch],
  );

  const onChangePage = useCallback(
    (page) => {
      setCurrentPage(page);

      const convertedParams = Object.fromEntries(searchParams.entries());

      const data = { ...convertedParams, ...filtersData, page: page?.toString() };

      for (const key in data) {
        if (!data[key]) {
          delete data[key];
        }
      }

      setSearchParams(data);
      dispatch(getAdminReviews(data));
    },
    [searchParams, filtersData, setSearchParams, dispatch],
  );

  const statuses = [
    { value: 'PENDING', label: t(`statuses:PENDING`) },
    { value: 'COMPLETE', label: t(`statuses:COMPLETE`) },
    { value: 'CANCEL', label: t(`statuses:CANCEL`) },
  ];

  const build = {
    title: t('adminReviews:main'),
    styled_by_status: true,
    table_data: adminReviews,
    table_meta: { ...adminReviewsMeta, current_page: currentPage, on_change_page: onChangePage },
    filters_data: filtersData,
    is_visible_add: false,
    set_filters_data: setFiltersData,
    on_submit_filters: onSubmitFilters,
    main_route: ROUTE.ADMIN.REVIEWS.MAIN,
    table_columns: [
      {
        id: 1,
        key: 'id',
        label: t('label:id'),
      },
      {
        id: 2,
        key: 'status',
        label: t('label:status'),
        value: ({ status }) => {
          return t(`statuses:${status}`);
        },
      },
      {
        id: 3,
        key: 'created_at',
        label: t('label:created'),
      },
      {
        id: 4,
        key: 'name',
        label: t('label:userName'),
      },
      {
        id: 5,
        key: 'login',
        label: t('label:login'),
        value: (item) => {
          return item?.message;
        },
      },
      {
        id: 6,
        key: 'message',
        label: t('label:review'),
        value: (item) => {
          return item?.message?.length > 50 ? item?.message?.slice(0, 50) + '...' : item?.message;
        },
      },
    ],
    filters: [
      {
        key: 'id',
        label: t('searchById'),
        type: 'input',
      },
      {
        key: 'name',
        label: t('searchByLogin'),
        type: 'input',
      },
      {
        key: 'email',
        label: t('searchByEmail'),
        type: 'input',
      },
      {
        key: 'status',
        label: t('status'),
        type: 'select',
        options: statuses,
      },
      {
        key: 'createdDate',
        label: t('createdDate'),
        type: 'datepicker',
      },
    ],
  };

  return { build };
};
